<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  messageId: { type: [Number, String], required: true },
  content: { type: String, default: '' },
});

const { t } = useI18n();
const store = useStore();
const dialogRef = ref(null);
const draft = ref('');
const isLoading = ref(false);

const trimmedDraft = computed(() => draft.value.trim());
const isUnchanged = computed(
  () => !trimmedDraft.value || trimmedDraft.value === props.content
);

const open = () => {
  draft.value = props.content || '';
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleConfirm = async () => {
  if (isUnchanged.value) return;

  isLoading.value = true;
  try {
    await store.dispatch('editMessage', {
      conversationId: props.conversationId,
      messageId: props.messageId,
      content: trimmedDraft.value,
    });
    useAlert(t('CONVERSATION.EDIT_MESSAGE.SUCCESS'));
    close();
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) || t('CONVERSATION.EDIT_MESSAGE.ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONVERSATION.EDIT_MESSAGE.TITLE')"
    :description="t('CONVERSATION.EDIT_MESSAGE.DESCRIPTION')"
    :confirm-button-label="t('CONVERSATION.EDIT_MESSAGE.SAVE')"
    :is-loading="isLoading"
    :disable-confirm-button="isUnchanged"
    @confirm="handleConfirm"
  >
    <TextArea
      v-model="draft"
      class="w-full"
      :placeholder="t('CONVERSATION.EDIT_MESSAGE.PLACEHOLDER')"
      :max-length="5000"
      show-character-count
      auto-height
    />
  </Dialog>
</template>
